<template>
    <v-layout>
        <v-flex xs12>
            <v-card>
                <v-card-title class="headline primary white--text">
                    {{ 'Automated Debug'|translate }}
                </v-card-title>
                <v-card-text v-if="loading">
                    <v-progress-linear :indeterminate="true"></v-progress-linear>

                </v-card-text>
                <v-card-text v-if="!loading">
                    <v-card-title class="accent white--text subheading">
                        {{ 'Mapping Integrity Issues'|translate }}
                        <v-spacer></v-spacer>
                        <v-btn class="accent" @click="exportIntegrityIssues">
                            <v-icon small class="mr-2">fa-download</v-icon>
                            {{ 'Download'|translate }}
                        </v-btn>
                        <div style="height: 24px; margin-right: 12px;">
                            <v-switch color="success"
                                      v-model="integrityErrorOnly"
                                      :label="'Error Only'|translate"
                            ></v-switch>
                        </div>
                    </v-card-title>
                    <v-data-table
                        :headers="integrityHeaders"
                        :items="refinedIntegrityIssues"
                        class="elevation-1"
                        :rows-per-page-items="[25,50,100,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
                        :items-per-page="25"
                    >
                        <template v-slot:items="props">
                            <td>{{ props.item.name }}</td>
                            <td>{{ props.item.type }}</td>
                            <td>
                                <v-chip :color="getColor(props.item.status)" style="color:#404040 !important;">
                                    {{ props.item.status }}
                                </v-chip>
                            </td>
                            <td>
                                <ul v-if="props.item.missingCriticalSensors">
                                    <li v-for="i in props.item.missingCriticalSensors">{{ i }}</li>
                                </ul>
                            </td>
                            <td>
                                <ul v-if="props.item.missingImportantSensors">
                                    <li v-for="i in props.item.missingImportantSensors">{{ i }}</li>
                                </ul>
                            </td>
                            <td>
                                <ul v-if="props.item.missingOptionalSensors">
                                    <li v-for="i in props.item.missingOptionalSensors">{{ i }}</li>
                                </ul>
                            </td>
                            <td>
                                <ul v-if="props.item.extraSensors">
                                    <li v-for="i in props.item.extraSensors">{{ i }}</li>
                                </ul>
                            </td>
                            <td>
                                <v-icon
                                    small
                                    @click="openPoints(props.item.name)"
                                >
                                    fa-edit
                                </v-icon>
                            </td>
                        </template>

                    </v-data-table>
                    <v-card-title class="accent white--text subheading">
                        {{ 'Duplicate Mappings'|translate }}
                        <v-spacer></v-spacer>
                    </v-card-title>
                    <v-data-table
                        :headers="duplicateHeaders"
                        :items="duplicateMappings"
                        class="elevation-1"
                        :rows-per-page-items="[25,50,100,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
                        :items-per-page="25"
                    >
                        <template v-slot:items="props">
                            <td>{{ props.item.id }}</td>
                            <td>
                                <ul v-if="props.item.occurrences">
                                    <li v-for="i in props.item.occurrences">{{ i }}</li>
                                </ul>
                            </td>

                            <td class="text-xs-left">
                                <v-icon
                                    small
                                    @click="openPoints(props.item.id)"
                                >
                                    fa-edit
                                </v-icon>
                            </td>
                        </template>
                    </v-data-table>
                </v-card-text>
                <v-card-text v-if="!loading">
                    <v-card-title class="accent white--text subheading">
                        {{ 'Custom View Configuration Issues'|translate }}
                    </v-card-title>
                    <v-data-table
                        :headers="headers"
                        :items="mcv"
                        class="elevation-1"
                        :rows-per-page-items="[25,50,100,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
                        :items-per-page="25"
                    >
                        <template v-slot:items="props">
                            <td>{{ props.item.name }}</td>
                            <td>
                                <ul v-if="props.item.issues">
                                    <li v-for="i in props.item.issues">{{ i }}</li>
                                </ul>
                            </td>
                            <td>
                                <v-icon class="mr-2"
                                        small
                                        @click="viewView(props.item)"
                                >
                                    fa-eye
                                </v-icon>
                                <v-icon class="mr-2"
                                        small
                                        @click="editView(props.item)"
                                >
                                    fa-edit
                                </v-icon>
                            </td>
                        </template>
                    </v-data-table>
                </v-card-text>
                <v-card-text v-if="!loading&&dataIssues">
                    <v-card-title class="accent white--text subheading">
                        {{ 'Data Issues'|translate }}
                        <v-spacer></v-spacer>
                        <v-btn class="accent" @click="exportUnitIssues">
                            <v-icon small class="mr-2">fa-download</v-icon>
                            {{ 'Download'|translate }}
                        </v-btn>
                        <div style="height: 24px; margin-right: 12px;">
                            <v-switch color="success"
                                      v-model="unitErrorOnly"
                                      :label="'Error Only'|translate"
                            ></v-switch>
                        </div>
                    </v-card-title>
                    <v-data-table
                        :headers="dataIssuesHeaders"
                        :items="refinedDataIssues"
                        class="elevation-1"
                        :rows-per-page-items="[25,50,100,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
                        :items-per-page="25"
                    >
                        <template v-slot:items="props">
                            <td>{{ props.item.equipment_name }}</td>
                            <td>{{ props.item.sensor_name }}</td>
                            <td>{{ props.item.issue }}</td>
                            <td>{{ props.item.flatline }}</td>
                            <td>{{ props.item.unit }}</td>
                            <td>{{ props.item.counts }}</td>
                            <td>{{ props.item.out_of_range_counts }}</td>
                            <td>{{ props.item.min }}</td>
                            <td>{{ props.item.max }}</td>
                            <td class="text-xs-left">
                                <v-icon
                                    small
                                    @click="dialogEquipmentName=props.item.equipment_name; dialogSensorName=props.item.sensor_name; dialog=true"
                                >
                                    fa-chart-line
                                </v-icon>
                            </td>
                        </template>
                    </v-data-table>
                    <v-dialog v-model="dialog" max-width="1400" persistent>
                        <v-card flat v-if="dialog">
                            <v-card-title class="primary white--text headline">{{ dialogEquipmentName }} -
                                {{ dialogSensorName }}
                            </v-card-title>
                            <v-card-text>
                                <date-range-selector v-model="dateRange"></date-range-selector>
                                <data3-chart
                                    :layout-overrides="{title:'',height:500,margin:layoutOverrides.margin,showlegend: false,hovermode:layoutOverrides.hovermode,hoverlabel:layoutOverrides.hoverlabel}"
                                    :on-only="false"
                                    :x-axis="'timestamp'"
                                    disable-responsive
                                    :show-export="false"
                                    ref="rvplots"
                                    just-plot
                                    :data-payload="payloadDef"
                                    :date-range="dateRange"
                                ></data3-chart>
                            </v-card-text>
                            <v-card-actions>
                                <v-spacer></v-spacer>
                                <v-btn @click="dialog=false">{{ 'Close'|translate }}</v-btn>
                            </v-card-actions>

                        </v-card>
                    </v-dialog>
                </v-card-text>
                <v-card-text v-if="!loading&&ruleIssues">
                    <v-card-title class="accent white--text subheading">
                        {{ 'Rule Issues'|translate }}
                        <v-spacer></v-spacer>
                        <v-btn class="accent" @click="exportRuleIssues">
                            <v-icon small class="mr-2">fa-download</v-icon>
                            {{ 'Download'|translate }}
                        </v-btn>
                        <div style="height: 24px; margin-right: 12px;">
                            <v-switch color="success"
                                      v-model="ruleErrorOnly"
                                      :label="'Error Only'|translate"
                            ></v-switch>
                        </div>
                    </v-card-title>
                    <v-data-table
                        :headers="ruleIssuesHeaders"
                        :items="refinedRuleIssues"
                        class="elevation-1"
                        :rows-per-page-items="[25,50,100,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
                        :items-per-page="25"
                    >
                        <template v-slot:items="props">
                            <td>{{ props.item.title }}</td>
                            <td>{{ props.item.rule_check }}</td>
                            <td>{{ props.item.rule }}</td>
                            <td>{{ props.item.equipment_type }}</td>
                            <td>{{ props.item.equipment_name }}</td>
                            <td>{{ props.item.relevant_variables }}</td>
                            <td>
                                <v-icon
                                    small
                                    @click="openRule(props.item)"
                                >
                                    fa-edit
                                </v-icon>
                            </td>
                        </template>
                    </v-data-table>
                </v-card-text>
            </v-card>
        </v-flex>
    </v-layout>
</template>

<script>
import {mapGetters} from 'vuex'
import {getPoint, getResult} from '@/helper'
import {exportCSV} from '../tocsv'
import integrity from '../config/pointMapping/integrity'
import Data3Chart from "@/components/Data3Chart";
import DateRangeSelector from "@/components/DateRangeSelector";
import _ from "lodash";
import moment from "moment";

export default {
    name: 'AutomatedDebug',
    components: {Data3Chart, DateRangeSelector},
    data() {
        return {
            loading: true,
            dialog: false,
            dialogSensorName: null,
            dialogEquipmentName: null,
            dateRange: {
                start: moment().subtract(30, "days").format('YYYY-MM-DD HH:mm:ss'),
                end: moment().format('YYYY-MM-DD HH:mm:ss')
            },
            errorColors: {
                'critical': 'red',
                'warning': 'yellow',
                'error': 'orange',
                'OK': 'green'
            },
            headers: [
                {text: this.$options.filters.translate('Name'), value: 'name'},
                {text: this.$options.filters.translate('Issues'), value: 'notes', sortable: false},
                {text: this.$options.filters.translate('Actions'), value: 'notes', sortable: false},
            ],
            duplicateHeaders: [
                {text: this.$options.filters.translate('Upstream ID'), value: 'id'},
                {text: this.$options.filters.translate('Occurrences'), value: 'occurrences', sortable: false},
                {text: this.$options.filters.translate('Actions'), value: 'notes', sortable: false},
            ],
            integrityHeaders: [
                {text: this.$options.filters.translate('Equipment Name'), value: 'name'},
                {text: this.$options.filters.translate('Equipment Type'), value: 'type'},
                {text: this.$options.filters.translate('Status'), value: 'status'},
                {
                    text: this.$options.filters.translate('Missing Critical Sensors'),
                    value: 'missingCriticalSensors',
                    sortable: false
                },
                {
                    text: this.$options.filters.translate('Missing Important Sensors'),
                    value: 'missingImportantSensors',
                    sortable: false
                },
                {
                    text: this.$options.filters.translate('Missing Optional Sensors'),
                    value: 'missingOptionalSensors',
                    sortable: false
                },
                {text: this.$options.filters.translate('Extra Sensors'), value: 'extraSensors', sortable: false},
                {text: this.$options.filters.translate('Actions'), value: 'name', sortable: false}
            ],
            mcv: [],
            dataIssues: [],
            ruleIssues: [],
            integrityIssues: [],
            duplicateMappings: [],
            unitErrorOnly: true,
            integrityErrorOnly: true,
            ruleErrorOnly: true,
            dataIssuesHeaders: [
                {text: this.$options.filters.translate('Equipment Name'), value: 'equipment_name'},
                {text: this.$options.filters.translate('Sensor Name'), value: 'sensor_name'},
                {text: this.$options.filters.translate('Issue'), value: 'issue'},
                {text: this.$options.filters.translate('Flatline'), value: 'flatline'},
                {text: this.$options.filters.translate('Unit'), value: 'unit'},
                {text: this.$options.filters.translate('Counts'), value: 'counts'},
                {text: this.$options.filters.translate('Out of range Counts'), value: 'out_of_range_counts'},
                {text: this.$options.filters.translate('Min'), value: 'min'},
                {text: this.$options.filters.translate('Max'), value: 'max'},
                {text: this.$options.filters.translate('Actions'), value: 'name', sortable: false}
            ],
            ruleIssuesHeaders: [
                {text: this.$options.filters.translate('Title'), value: 'title'},
                {text: this.$options.filters.translate('Issue'), value: 'rule_check'},
                {text: this.$options.filters.translate('Rule'), value: 'rule'},
                {text: this.$options.filters.translate('Equipment Type'), value: 'equipment_type'},
                {text: this.$options.filters.translate('Equipment Name'), value: 'equipment_name'},
                {text: this.$options.filters.translate('Relevant Variables'), value: 'relevant_variables'},
                {text: this.$options.filters.translate('Actions'), value: 'name', sortable: false}
            ],
            layoutOverrides: {
                xaxis: {
                    title: '',
                },
                yaxis: {
                    fixedrange: true
                },
                margin: {
                    t: 0,
                    l: 0,
                    r: 0,
                    b: 0
                },
                height: 300,
                legend: {
                    orientation: 'h',
                    y: -0.2,
                },
                hoverlabel: {
                    namelength: -1
                },
                hovermode: "x",
            }
        }
    },
    computed: {
        ...mapGetters([
            'publishedCustomViews', 'mappingIntegrityConfig'
        ]),
        payloadDef() {
            return [{
                'filters': {
                    equipment_name: this.dialogEquipmentName,
                    sensor_name: this.dialogSensorName,
                    facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
                },
                pipeline: [],
                seriesOverrides: {
                    type: 'scatter',
                    opacity: 1,
                    xbins: {
                        size: 1
                    },
                    ybins: {
                        size: 1
                    }
                },
            }]
        },
        refinedDataIssues() {
            if (this.dataIssues && this.unitErrorOnly) {
                return this.dataIssues.filter(x => x.error_level === "Error")
            } else {
                return this.dataIssues
            }
        },
        refinedRuleIssues() {
            if (this.ruleIssues && this.ruleErrorOnly) {
                return this.ruleIssues.filter(x => x.error_level === "Error")
            } else {
                return this.ruleIssues
            }
        },
        refinedIntegrityIssues() {
            if (this.integrityIssues && this.integrityErrorOnly) {
                return this.integrityIssues.filter(x => x.status !== "OK")
            } else {
                return this.integrityIssues
            }
        }
    },
    mounted() {
        let maxDate = this.$store.state.pointsListStore.pointsList.data_max_date;
        if (maxDate) {
            this.dateRange = {
                start: moment(maxDate).subtract(30, "days").format('YYYY-MM-DD HH:mm:ss'),
                end: moment(maxDate).format('YYYY-MM-DD HH:mm:ss')
            }
        }
        let me = this
        getResult({
            facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
            result_name: 'unit_checking'
        }).then(function (res) {
            if (res && res.result && res.result[0]) {
                me.dataIssues = res.result[0].content.filter(x => x.issue)
            }
        })
        getResult({
            facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
            result_name: 'rule_checking'
        }).then(function (res) {
            if (res && res.result && res.result[0]) {
                me.ruleIssues = res.result[0].content.filter(x => x.rule_check)
            }
        })
        getPoint({
            facility_name: this.$store.state.pointsListStore.pointsList.facility_name,
        }).then(function (res) {
            if (res && res.result) {
                let duplicateIds=JSON.parse(JSON.stringify([...new Set(me.findDuplicates(res.result.filter(x=>x.haystack_id).map(x=>x.haystack_id)))]))
                me.duplicateMappings=duplicateIds.map(id=>{return{
                    id:id,
                    occurrences:res.result.filter(x=>x.haystack_id===id).map(x=>x.equipment_name+ " - "+x.sensor_name)
                }})
            }
        })
        let combinations = this.$store.state.pointsListStore.pointsList.points_list.map(sensor => {
            return {
                sensor_name: sensor.sensor_name,
                equipment_name: sensor.equipment_name,
                equipment_type: this.$store.state.pointsListStore.pointsList.equipment_details[sensor.equipment_name].equipment_type,
                equipment_tags: this.$store.state.pointsListStore.pointsList.equipment_details[sensor.equipment_name].equipment_tags ? this.$store.state.pointsListStore.pointsList.equipment_details[sensor.equipment_name].equipment_tags : [],
                floor: this.$store.state.pointsListStore.pointsList.equipment_details[sensor.equipment_name].floor,
            }
        })
        let issues = {}
        for (let customView of this.publishedCustomViews) {
            if (customView.plotList) {
                for (let item of customView.plotList) {
                    if (item.type === "data3-chart") {
                        for (let query of item.config["data-payload"]) {
                            let matchingItems = combinations.filter(x => {
                                for (let key of Object.keys(query.filters)) {
                                    if (x[key] !== query.filters[key]) {
                                        return false
                                    }
                                }
                                return true
                            })
                            if (matchingItems.length === 0) {
                                if (!issues[customView.id]) {
                                    issues[customView.id] = {
                                        name: customView.title,
                                        id: customView.id,
                                        issues: []
                                    }
                                }
                                let issue = "No sensors matching filters :"
                                let first = true
                                for (let key of Object.keys(query.filters)) {
                                    if (!first) {
                                        issue = issue + ","
                                    }
                                    issue = issue + " " + key + " = " + query.filters[key]
                                    first = false
                                }
                                if (issues[customView.id].issues.indexOf(issue) < 0) {
                                    issues[customView.id].issues.push(issue)
                                }
                            }
                        }
                    }
                }
            }
        }
        issues = Object.values(issues)
        this.mcv = issues
        let integrityIssues = []
        let integrityConfig = this.mappingIntegrityConfig ? this.mappingIntegrityConfig : integrity
        for (let index in this.$store.state.pointsListStore.pointsList.equipment_details) {
            let equip = this.$store.state.pointsListStore.pointsList.equipment_details[index]
            let issuesForType = integrityConfig.filter(x => x.equipmentGroup === equip.equipment_type)
            if (issuesForType.length > 0) {
                let equipSensors = this.$store.state.pointsListStore.pointsList.sensors[index]
                let interityIssue = {
                    name: equip.equipment_name,
                    id: equip.equipment_id,
                    type: equip.equipment_type,
                    status: "OK",
                    missingCriticalSensors: issuesForType.filter(x => x.importance === "critical").map(x => x.carnotname).filter(x => !equipSensors.includes(x)),
                    missingImportantSensors: issuesForType.filter(x => x.importance === "important").map(x => x.carnotname).filter(x => !equipSensors.includes(x)),
                    missingOptionalSensors: issuesForType.filter(x => x.importance === "optional").map(x => x.carnotname).filter(x => !equipSensors.includes(x)),
                }
                let allHandledNames = issuesForType.map(x => x.carnotname)
                interityIssue.extraSensors = equipSensors.filter(x => !allHandledNames.includes(x))
                if (interityIssue.missingCriticalSensors.length > 0) {
                    interityIssue.status = "Critical"
                } else if (interityIssue.missingImportantSensors.length > 0) {
                    interityIssue.status = "Error"
                } else if (interityIssue.missingOptionalSensors.length > 0) {
                    interityIssue.status = "Warning"
                }
                integrityIssues.push(interityIssue)
            }
        }
        this.integrityIssues = integrityIssues
        this.loading = false
    },
    methods: {
        findDuplicates(arr){
            let sorted_arr = arr.slice().sort();
            let results = [];
            for (let i = 0; i < sorted_arr.length - 1; i++) {
                if (sorted_arr[i + 1] === sorted_arr[i]) {
                    results.push(sorted_arr[i]);
                }
            }
            return results;
        },
        getColor(x) {
            return _.get(this.errorColors, x.toLowerCase(), 'green')
        },
        exportRuleIssues() {
            exportCSV(this.ruleIssues)
        },
        exportUnitIssues() {
            exportCSV(this.dataIssues)
        },
        exportIntegrityIssues() {
            exportCSV(this.integrityIssues)
        },
        editView(view) {
            let output = {
                path: '/custom-view-builder',
                query: {
                    view: view.id,
                }
            }
            if (process.env.VUE_APP_SINGLE_WINDOW_MODE){
                this.$router.push(output)
            } else {
                window.open(this.$router.resolve(output).href)
            }
        }, openRule(rule) {
            let output = {
                path: '/rules',
                query: {
                    title: rule.title,
                    equipment_type: rule.equipment_type,
                }
            }
            if (process.env.VUE_APP_SINGLE_WINDOW_MODE){
                this.$router.push(output)
            } else {
                window.open(this.$router.resolve(output).href)
            }
        }, openPoints(item) {
            let output = {
                path: '/points',
                query: {
                    search: item,
                }
            }
            if (process.env.VUE_APP_SINGLE_WINDOW_MODE){
                this.$router.push(output)
            } else {
                window.open(this.$router.resolve(output).href)
            }
        }, viewView(view) {
            let output = {
                path: '/user-automated/' + view.id,
            }
            if (process.env.VUE_APP_SINGLE_WINDOW_MODE){
                this.$router.push(output)
            } else {
                window.open(this.$router.resolve(output).href)
            }
        },
    }
}
</script>

<style scoped>

</style>
